/**
 * Journey: IVF, Solo, Male
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * For a man building his family independently — requires both an egg donor
 * and a gestational surrogate. Very similar to ivf-surrogate-male.ts but
 * emphasizes the independent/solo aspect with no partner language.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IVF Works for You",
  subtitle: "Building your family independently",
  icon: "BookOpen",
  education: {
    context:
      "You're building your family through IVF with an egg donor and a gestational surrogate. You provide the sperm, a donor provides the eggs, and the surrogate carries the pregnancy. Neither the donor nor the surrogate has parental rights.",
    details:
      "The full process:\n1. Egg donor stimulation (10-14 days) — Your donor takes injectable medications to mature multiple eggs\n2. Egg retrieval (20 minutes) — Donor's eggs are collected under light sedation\n3. Your sperm sample (retrieval day) — You provide a fresh sample at the clinic\n4. Fertilization (day 0) — Eggs and your sperm are combined in the lab (or ICSI is used)\n5. Embryo culture (5-7 days) — Embryos develop to blastocyst stage\n6. Embryo transfer (5 minutes) — A single embryo is transferred to your surrogate's uterus\n7. Two-week wait — Your surrogate waits ~14 days for a blood pregnancy test (beta hCG)\n\nYour role: select your egg donor and surrogate, provide your sperm sample, and coordinate the process.",
    learnMoreLink: "ivf-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IVF guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "ivf-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "male-factor",
      additionalEducation:
        "Your semen analysis suggests male factor infertility. Your clinic will likely recommend ICSI (intracytoplasmic sperm injection), where a single healthy sperm is injected directly into each egg. ICSI bypasses natural fertilization barriers and is very effective — fertilization rates are 70-80%.",
    },
    {
      condition: "age-38-plus",
      additionalEducation:
        "After 40, sperm DNA fragmentation increases, which can affect embryo quality. Your clinic may recommend DNA fragmentation testing. Lifestyle optimization (avoiding heat, limiting alcohol, supplements) can help reduce fragmentation.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 39],
      content:
        "Male age has a modest effect on IVF outcomes. Sperm quality is generally well-maintained through your 30s. Using donor eggs and a surrogate means egg quality and uterine factors are optimized.",
    },
    {
      range: [40, 44],
      content:
        "After 40, sperm DNA fragmentation increases slightly. Your clinic may recommend a DNA fragmentation test alongside the standard semen analysis.",
    },
    {
      range: [45, 99],
      content:
        "Over 45, sperm DNA fragmentation is higher. Your clinic may discuss DNA fragmentation testing and whether ICSI would benefit your cycle.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Your Fertility Testing",
  subtitle: "Sperm analysis and screening",
  icon: "TestTube",
  education: {
    context:
      "Your semen analysis determines the fertilization method (conventional vs. ICSI). Your egg donor and surrogate are screened separately by the agency or clinic — you don't need to coordinate their testing.",
  },
  actions: [
    {
      label: "Find a lab near you",
      variant: "primary",
      screen: "FindCareScreen",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "semen-analysis",
      title: "Semen analysis",
      education:
        "Measures count, motility, and morphology. Results determine if conventional fertilization or ICSI is needed. Abstain 2-5 days before your appointment.",
      status: "not-started",
    },
    {
      id: "dna-fragmentation",
      title: "DNA fragmentation test (if recommended)",
      education:
        "Measures sperm DNA damage. High fragmentation (>30%) may warrant lifestyle changes or ICSI. Not always needed — your clinic will advise.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required before any IVF cycle. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
  optimizationWindow: {
    substepId: "semen-analysis",
    biologicalCycle: "~74 days for full sperm regeneration",
    baselineAction: "Get a baseline semen analysis",
    optimizationPeriod: "2-3 months",
    retestAction: "Retest to see improvement",
    lifestyle: [
      {
        title: "Avoid excessive heat",
        evidence: "strong",
        impact: "high",
        education:
          "Sperm production requires temperatures 2-4°C below body temperature. Hot tubs, saunas, tight underwear, and laptops on your lap all raise scrotal temperature.",
      },
      {
        title: "Limit alcohol",
        evidence: "strong",
        impact: "moderate",
        education:
          "Heavy alcohol use reduces testosterone and impairs sperm production. Limiting intake during your optimization window is prudent.",
      },
      {
        title: "Exercise regularly",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Regular moderate exercise improves sperm parameters. Avoid extreme endurance exercise and heavy cycling, which can temporarily lower counts.",
      },
      {
        title: "Quit smoking/vaping",
        evidence: "strong",
        impact: "high",
        education:
          "Tobacco reduces sperm count by 20-30% and increases DNA fragmentation. Cannabis also impairs motility and morphology.",
      },
    ],
    supplements: [
      {
        title: "CoQ10 (200-400mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports mitochondrial function in sperm cells. Multiple studies show improvements in count and motility after 3 months.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Zinc (30mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Essential for testosterone production and sperm development. Seminal fluid has 100x the zinc concentration of blood.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Folate/L-methylfolate (400-800mcg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports DNA synthesis in developing sperm. Combined with zinc, studies show improved count.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Vitamin D (2000-4000 IU/day)",
        evidence: "moderate",
        impact: "low",
        education:
          "Vitamin D receptors exist on sperm cells. Deficiency is associated with lower motility.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
    ],
    ageGuidance: [
      {
        bracket: "Under 40",
        range: [0, 39],
        recommendation: "optimize-then-start",
        explanation:
          "You have time to optimize. A 2-3 month window of supplements and lifestyle changes can meaningfully improve sperm quality. This aligns well with the time needed for donor and surrogate coordination.",
      },
      {
        bracket: "40-45",
        range: [40, 45],
        recommendation: "parallel",
        explanation:
          "Start optimizing now while coordinating with your donor and surrogate. The timelines often align well.",
      },
      {
        bracket: "Over 45",
        range: [46, 99],
        recommendation: "start-now-optimize-alongside",
        explanation:
          "Don't delay the cycle for optimization. Start supplements and lifestyle changes now, but proceed with the cycle timeline.",
      },
    ],
  },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Choose a clinic experienced with surrogacy",
  icon: "Building2",
  education: {
    context:
      "For surrogacy IVF with donor eggs, look for clinics experienced in third-party reproduction. Check SART.org for donor egg success rates. Consider clinics that coordinate with both egg donor agencies and surrogacy agencies — this can simplify the process significantly.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "ivf" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education:
        "Compare SART donor egg success rates. Ask about their surrogacy and third-party reproduction experience.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education: "30-60 minute initial consultation to discuss your plan.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education: "Your clinic will start the onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Donor, surrogate, legal, and medical prep",
  icon: "ClipboardCheck",
  education: {
    context:
      "Preparation involves selecting your egg donor and surrogate, completing legal agreements, your medical history, and decisions about genetic testing. This is the most coordination-heavy phase.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "select-egg-donor",
      title: "Select your egg donor",
      education:
        "Choose a donor through an agency or your clinic's donor program. Review profiles with physical characteristics, education, health history, and genetic screening results.",
      status: "not-started",
    },
    {
      id: "match-surrogate",
      title: "Match with a gestational surrogate",
      education:
        "Work with a surrogacy agency to find a gestational carrier. Matching typically takes 1-3 months and includes medical and psychological screening.",
      status: "not-started",
    },
    {
      id: "legal-agreements",
      title: "Legal agreements for both",
      education:
        "Separate agreements needed for egg donor and surrogate. All parties need independent legal counsel. Covers compensation, medical decisions, parental rights, and expectations. Must be completed before any medical procedures begin.",
      status: "not-started",
    },
    {
      id: "medical-history",
      title: "Complete your medical history",
      education:
        "Your health history is needed for consent forms and to identify any factors that affect sperm quality.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education:
        "Needed for billing. Your surrogate may have separate insurance for the pregnancy.",
      status: "not-started",
    },
    {
      id: "pgta-decision",
      title: "PGT-A decision",
      education:
        "Decide whether to genetically test embryos before transfer. Cost: $3,000-$5,000. Recommended for surrogacy to maximize success with each transfer.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Covers procedure, risks, embryo disposition, and third-party reproduction consents.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements for sperm quality (optional)",
      education:
        "CoQ10, zinc, folate, and vitamin D support sperm quality. Start 2-3 months before if possible — sperm takes ~74 days to regenerate.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IVF Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your cycle is active. Track appointments and key moments in the Care section.",
    details:
      "Your donor's cycle: stimulation (10-14 days) → trigger → egg retrieval.\n\nThen: fertilization with your sperm → embryo development (5-7 days).\n\nYour surrogate's cycle: preparation → embryo transfer → two-week wait.\n\nYour key moment: providing your sperm sample on retrieval day.\n\nYour Care page has the shared appointment calendar and your sample instructions.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between transfer and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "The two-week wait (TWW) is the period between embryo transfer to your surrogate and the pregnancy blood test.",
    details:
      "Here's what's happening:\nDays 1-3: The embryo hatches and begins attaching to your surrogate's uterine lining\nDays 4-5: Implantation begins\nDays 6-8: hCG hormone starts being produced if implantation is successful\nDays 9-14: hCG levels rise enough to be detectable by blood test\n\nThis wait is unique because the physical process is happening in someone else's body. You may feel a mix of hope, anxiety, and a sense of limited control — all of that is completely normal.\n\nYour surrogate will follow her clinic's instructions for progesterone and activity. Stay in touch, be supportive, and trust the process. The blood test (beta hCG) is the only reliable answer.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "Support resources", variant: "secondary", icon: "Users" },
    { label: "When is the blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IVF_SOLO_MALE: JourneyDefinition = {
  key: "ivf-solo-male",
  gender: "male",
  goal: "pregnancy",
  pathway: "solo",
  title: "IVF",
  description: "Building your family with donor eggs and a surrogate",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
